import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Chip,
  Pagination,
  Divider,
  List,
  ListItem,
  Paper,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import { MAIL_INGESTION_LOGS_ENDPOINT, ERROR_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../../components/ui/customCircularProgress/customCircularProgress.component";
import { formatDateTime } from "../../utils";

export interface MailIngestionLog {
  id: number;
  executed_at: string;
  messages_fetched: number;
  messages_parsed: number;
  counters_created: number;
  counters_created_from_pdf: number;
  counters_created_from_counter_report: number;
  errors: unknown[];
}

interface MailIngestionLogsProps {
  refreshKey?: number;
}

const PAGE_SIZE = 3;

// Error lines arrive from the backend already rendered as readable, localized
// sentences (see MailIngestionLogSerializer). This only guards against a
// non-string slipping through from older log rows.
const formatLogError = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    if ("message" in error) {
      return String((error as { message: unknown }).message);
    }

    if ("error" in error) {
      return String((error as { error: unknown }).error);
    }

    return JSON.stringify(error);
  }

  return String(error);
};

const LogCard = ({ log }: { log: MailIngestionLog }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasErrors = log.errors.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, width: "100%", borderRadius: "var(--defaultRadius)" }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="subtitle2" fontWeight="bold">
          {formatDateTime(log.executed_at)}
        </Typography>
        <Chip
          label={hasErrors ? t("With errors") : t("Success")}
          color={hasErrors ? "error" : "success"}
          size="small"
        />
      </Box>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {t("Fetched")}: <strong>{log.messages_fetched}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Parsed")}: <strong>{log.messages_parsed}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Counters created")}: <strong>{log.counters_created}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("From PDFs")}: <strong>{log.counters_created_from_pdf}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("From counter reports")}:{" "}
          <strong>{log.counters_created_from_counter_report}</strong>
        </Typography>
      </Box>
      {hasErrors && (
        <Box mt={1}>
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              color: "error.main",
              font: "inherit",
            }}
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
            <Typography variant="caption" color="error">
              {expanded ? t("Hide errors") : t("Show errors ({{count}})", { count: log.errors.length })}
            </Typography>
          </Box>
          <Collapse in={expanded} unmountOnExit>
            <Box mt={0.5}>
              {log.errors.map((err, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  display="block"
                  color="error"
                  sx={{ ml: 1 }}
                >
                  {formatLogError(err)}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
};

export const MailIngestionLogs = ({ refreshKey = 0 }: MailIngestionLogsProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [logs, setLogs] = useState<MailIngestionLog[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const lastHandledRefreshKey = useRef(refreshKey);

  const fetchLogs = useCallback(async (currentPage: number) => {
    setLoading(true);
    try {
      const response = await axios.get(MAIL_INGESTION_LOGS_ENDPOINT, {
        params: { page: currentPage, page_size: PAGE_SIZE },
      });
      setLogs(response.data.results ?? []);
      setCount(response.data.count ?? 0);
    } catch {
      setLogs([]);
      setCount(0);
      enqueueSnackbar(t("Failed to fetch history of counters ingestion"), ERROR_SNACKBAR);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchLogs(page);
  }, [fetchLogs, page]);

  useEffect(() => {
    if (refreshKey === 0 || refreshKey === lastHandledRefreshKey.current) {
      return;
    }

    lastHandledRefreshKey.current = refreshKey;

    if (page === 1) {
      fetchLogs(1);
      return;
    }

    setPage(1);
  }, [fetchLogs, page, refreshKey]);

  const totalPages = Math.ceil(count / PAGE_SIZE);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CustomCircularProgress />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          {t("No history of counters ingestion available.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <List disablePadding>
        {logs.map((log, index) => (
          <Box key={log.id}>
            <ListItem disablePadding sx={{ py: 1.5 }}>
              <LogCard log={log} />
            </ListItem>
            {index < logs.length - 1 && <Divider />}
          </Box>
        ))}
      </List>
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};
